/**
 * StreamingProgress - 4-step horizontal progress bar for Sarah pipeline
 * Replaces generic CircularProgress spinner in AgentSimple.tsx
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Check as CheckIcon,
} from '@mui/icons-material';

export type PipelineStage = 'idle' | 'understanding' | 'routing' | 'retrieving' | 'generating';

interface StageConfig {
  key: PipelineStage;
  label: string;
}

const STAGES: StageConfig[] = [
  { key: 'understanding', label: 'Understanding' },
  { key: 'routing', label: 'Finding docs' },
  { key: 'retrieving', label: 'Reading blocks' },
  { key: 'generating', label: 'Generating' },
];

const stageIndex = (stage: PipelineStage): number => {
  const idx = STAGES.findIndex(s => s.key === stage);
  return idx >= 0 ? idx : -1;
};

interface StreamingProgressProps {
  currentStage: PipelineStage;
  stageDetails: Partial<Record<PipelineStage, string>>;
  collapsed: boolean;
}

const StreamingProgress: React.FC<StreamingProgressProps> = ({ currentStage, stageDetails, collapsed }) => {
  if (currentStage === 'idle') return null;

  const activeIdx = stageIndex(currentStage);

  // Collapsed mode: single-line compact view
  if (collapsed) {
    const detail = stageDetails[currentStage] || '';
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, px: 1 }}>
        <Box sx={{
          display: 'flex', gap: 0.5, alignItems: 'center',
        }}>
          {STAGES.map((s, i) => (
            <Box
              key={s.key}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: i < activeIdx ? '#4caf50' : i === activeIdx ? '#1976d2' : '#e0e0e0',
                animation: i === activeIdx ? 'pulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                  '50%': { opacity: 0.5, transform: 'scale(1.3)' },
                },
              }}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap>
          {STAGES[activeIdx]?.label}{detail ? ` - ${detail}` : ''}
        </Typography>
      </Box>
    );
  }

  // Expanded mode: full horizontal stepper
  return (
    <Box sx={{ mb: 3, px: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {STAGES.map((stage, i) => {
          const isCompleted = i < activeIdx;
          const isActive = i === activeIdx;
          const isPending = i > activeIdx;
          const detail = stageDetails[stage.key];

          return (
            <React.Fragment key={stage.key}>
              {/* Step indicator */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80, flex: 1 }}>
                {/* Dot / Check */}
                <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isCompleted ? '#4caf50' : isActive ? '#1976d2' : '#e0e0e0',
                  color: 'white',
                  transition: 'all 0.3s ease',
                  animation: isActive ? 'pulse 1.5s ease-in-out infinite' : 'none',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(25,118,210,0.4)' },
                    '50%': { opacity: 0.85, boxShadow: '0 0 0 6px rgba(25,118,210,0)' },
                  },
                }}>
                  {isCompleted ? (
                    <CheckIcon sx={{ fontSize: 14 }} />
                  ) : (
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: isPending ? '#bdbdbd' : 'white',
                    }} />
                  )}
                </Box>

                {/* Label */}
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    fontWeight: isActive ? 600 : 400,
                    color: isCompleted ? '#4caf50' : isActive ? '#1976d2' : '#999',
                    fontSize: '0.7rem',
                    textAlign: 'center',
                  }}
                >
                  {stage.label}
                </Typography>

                {/* Detail subtitle */}
                {isActive && detail && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '0.65rem',
                      textAlign: 'center',
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {detail}
                  </Typography>
                )}
                {isCompleted && detail && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#999',
                      fontSize: '0.6rem',
                      textAlign: 'center',
                      maxWidth: 120,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {detail}
                  </Typography>
                )}
              </Box>

              {/* Connector line */}
              {i < STAGES.length - 1 && (
                <Box sx={{
                  flex: 1,
                  height: 2,
                  bgcolor: i < activeIdx ? '#4caf50' : '#e0e0e0',
                  transition: 'background-color 0.3s ease',
                  mt: -2,  // Align with dots
                }} />
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default StreamingProgress;
